import {
  Color,
  Flex,
  TextField,
  IconEnum,
  Slider,
  Icon,
} from 'component-library';
import React, {
  ChangeEvent,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(() => ({
  slider: {
    maxWidth: 400,
  },
  percentTextField: {
    minWidth: '80px',
    marginRight: '8px',
  },
  inputContainer: {
    width: 80,
    marginRight: 8,
  },
  percentInput: {
    textAlign: 'center',
    border: `1px solid ${Color.neutral._20} !important`,
    boxShadow: 'none !important',
    height: 48,
  },
  outOfBounds: {
    textAlign: 'left',
  },
}));

interface RdPercentageSliderProps {
  highPercentEstimate: number;
  lowPercentEstimate: number;
  percent: number;
  setPercent: (id: number, newPercentage: number) => void;
  id: string | number;
}

export const RdPercentageSlider = ({
  highPercentEstimate,
  lowPercentEstimate,
  percent,
  setPercent,
  id,
}: RdPercentageSliderProps) => {
  const classes = useStyles();
  const numericId = Number(id);

  // Local state for smooth dragging - only syncs to parent on commit
  const [localValue, setLocalValue] = useState(percent || 0);

  // Sync local value when parent prop changes (e.g., from text input or initial load)
  useEffect(() => {
    setLocalValue(percent || 0);
  }, [percent]);

  // Derive out-of-bounds status from local value for immediate feedback
  const percentOutOfBounds = useMemo(
    () => localValue < lowPercentEstimate || localValue > highPercentEstimate,
    [localValue, lowPercentEstimate, highPercentEstimate],
  );

  // Handle slider changes - only update local state for smooth dragging
  const handleSliderChange = useCallback((value: number) => {
    setLocalValue(value);
  }, []);

  // Handle slider commit (mouse up, touch end, key up) - sync to parent
  // Note: checkPercentages is handled by useEffect in JobGroupCard when employees changes
  const handleSliderCommit = useCallback(
    (value: number) => {
      setPercent(numericId, value);
    },
    [numericId, setPercent],
  );

  // Handle text field blur - parse and commit the value
  // Note: checkPercentages is handled by useEffect in JobGroupCard when employees changes
  const handlePercentInput = useCallback(
    (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
      const parsed = parseInt(e.target.value.replace(/\D/g, ''), 10);
      const validValue = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
      setLocalValue(validValue);
      setPercent(numericId, validValue);
    },
    [numericId, setPercent],
  );

  return (
    <Flex
      alignItems='center'
      justifyContent='flex-end'
      padding={[8, 0, 8, 16]}
      gap={16}
    >
      {percentOutOfBounds && (
        <Icon name={IconEnum.info_circle} color={Color.blue._70} />
      )}
      <Slider
        dataTestId='rd-percentage-slider'
        value={localValue}
        onChange={handleSliderChange}
        onCommitChange={handleSliderCommit}
        aria-label='R&D activities percentage'
        className={classes.slider}
      />
      <Flex.Cell className={classes.inputContainer}>
        <TextField
          value={localValue}
          onBlur={handlePercentInput}
          percentFormat
          className={classes.percentTextField}
          inputClassName={`${classes.percentInput} ${
            percentOutOfBounds ? classes.outOfBounds : ''
          }`}
        />
      </Flex.Cell>
    </Flex>
  );
};
